import React, { useState, useEffect, useCallback } from 'react';
import { listReviews, addReview, updateReview, deleteReview } from '../../lib/reviews';

const RatingSelect = ({ value, onChange }) => (
  <select value={value} onChange={(e) => onChange(Number(e.target.value))} className="px-3 py-2 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent">
    {[5, 4, 3, 2, 1].map((n) => (
      <option key={n} value={n}>{'★'.repeat(n)} ({n})</option>
    ))}
  </select>
);

const ReviewCard = ({ review, onSaved, onDeleted }) => {
  const [name, setName] = useState(review.name);
  const [tag, setTag] = useState(review.tag);
  const [quote, setQuote] = useState(review.quote);
  const [rating, setRating] = useState(review.rating);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');

  const dirty = name !== review.name || tag !== review.tag || quote !== review.quote || rating !== review.rating;

  const save = async () => {
    setBusy(true);
    setStatus('');
    try {
      await updateReview(review.id, { name, tag, quote, rating });
      setStatus('✓ Sparat');
      onSaved();
    } catch (err) {
      setStatus('Fel: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Ta bort denna recension permanent?')) return;
    await deleteReview(review.id);
    onDeleted();
  };

  return (
    <div className="bg-cv-base border border-cv-border flex flex-col gap-3" style={{ borderRadius: 'var(--cv-radius)', padding: 20 }}>
      <div className="flex gap-3 flex-wrap">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Namn" className="flex-1 min-w-[200px] px-3 py-2 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent" />
        <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Typ (t.ex. Bröllopsdukning)" className="flex-1 min-w-[180px] px-3 py-2 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent" />
        <RatingSelect value={rating} onChange={setRating} />
      </div>
      <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={4} placeholder="Recensionstext" className="px-3 py-2 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent resize-y" />
      <div className="flex gap-2 items-center">
        {dirty && (
          <button type="button" onClick={save} disabled={busy} className="bg-cv-ink text-cv-base border-none px-5 py-2 text-xs tracking-[0.12em] uppercase rounded-cv cursor-pointer hover:bg-cv-ink2 transition-colors disabled:opacity-60">Spara</button>
        )}
        <button type="button" onClick={remove} className="bg-transparent border border-red-200 text-red-700 px-5 py-2 text-xs tracking-[0.12em] uppercase rounded-cv cursor-pointer hover:bg-red-50 transition-colors ml-auto">Ta bort</button>
      </div>
      {status && <p className="m-0 text-xs text-cv-ink2">{status}</p>}
    </div>
  );
};

export const ReviewsManager = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');

  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [quote, setQuote] = useState('');
  const [rating, setRating] = useState(5);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReviews(await listReviews());
    } catch (err) {
      setStatus('Kunde inte hämta recensioner: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !quote.trim()) {
      setStatus('Fyll i minst namn och recensionstext.');
      return;
    }
    setSaving(true);
    setStatus('');
    try {
      await addReview({ name: name.trim(), tag: tag.trim(), quote: quote.trim(), rating });
      setStatus('✓ Recensionen är tillagd');
      setName('');
      setTag('');
      setQuote('');
      setRating(5);
      await load();
    } catch (err) {
      setStatus('Något gick fel: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-cv-panel border border-cv-border" style={{ borderRadius: 'var(--cv-radius)', padding: 20 }}>
        <p className="m-0 text-sm text-cv-muted leading-[1.7]">
          Här hanterar du kundomdömena som visas på startsidan. Lägg till nya, ändra text eller betyg, och ta bort gamla.
          Initialerna i cirkeln skapas automatiskt från namnet.
        </p>
      </div>

      <form onSubmit={add} className="bg-cv-base border border-cv-border flex flex-col gap-4" style={{ borderRadius: 'var(--cv-radius)', padding: 24 }}>
        <h3 className="m-0 font-serif font-medium text-[20px] text-cv-ink">Lägg till recension</h3>
        <div className="flex gap-3 flex-wrap">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kundens namn" className="flex-1 min-w-[200px] px-3 py-2.5 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent" />
          <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Typ (t.ex. Bröllopsdukning)" className="flex-1 min-w-[180px] px-3 py-2.5 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent" />
          <RatingSelect value={rating} onChange={setRating} />
        </div>
        <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={4} placeholder="Vad skrev kunden?" className="px-3 py-2.5 bg-cv-base border border-cv-edge rounded-cv text-cv-ink outline-none focus:border-cv-accent resize-y" />
        <button type="submit" disabled={saving} className="w-fit bg-cv-ink text-cv-base border-none px-6 py-3 text-[13px] tracking-[0.14em] uppercase rounded-cv cursor-pointer hover:bg-cv-ink2 transition-colors disabled:opacity-60">
          {saving ? 'Sparar…' : 'Lägg till recension'}
        </button>
        {status && <p className="m-0 text-sm text-cv-ink2">{status}</p>}
      </form>

      {loading ? (
        <p className="text-cv-muted">Laddar recensioner…</p>
      ) : reviews.length === 0 ? (
        <p className="text-cv-muted text-sm leading-[1.7]">
          Inga recensioner i databasen ännu. Kör seed-scriptet (se SETUP-SUPABASE.md) så läggs de nuvarande två till, eller skriv en ny ovan.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} onSaved={load} onDeleted={load} />
          ))}
        </div>
      )}
    </div>
  );
};
